/**
 * Vehicle type definitions (spec section 10).
 *
 * Durations are in game-minutes. priceSensitivity / walkingSensitivity /
 * serviceExpectation are 0..1 and are consumed by later phases (demand,
 * reputation). `spawnWeight` biases the random type picker in Phase 3.
 */
export interface VehicleTypeDef {
  id: string;
  name: string;
  color: number;
  priceSensitivity: number;
  walkingSensitivity: number;
  serviceExpectation: number;
  minDurationMinutes: number;
  maxDurationMinutes: number;
  evRequired: boolean;
  spawnWeight: number;
}

export const VEHICLE_TYPES: Record<string, VehicleTypeDef> = {
  office: {
    id: 'office',
    name: 'Office Worker',
    color: 0x42a5f5,
    priceSensitivity: 0.8,
    walkingSensitivity: 0.5,
    serviceExpectation: 0.4,
    minDurationMinutes: 7 * 60,
    maxDurationMinutes: 10 * 60,
    evRequired: false,
    spawnWeight: 3,
  },
  shopper: {
    id: 'shopper',
    name: 'Shopper',
    color: 0xff7043,
    priceSensitivity: 0.5,
    walkingSensitivity: 0.8,
    serviceExpectation: 0.5,
    minDurationMinutes: 1 * 60,
    maxDurationMinutes: 3 * 60,
    evRequired: false,
    spawnWeight: 4,
  },
  hospital: {
    id: 'hospital',
    name: 'Hospital Visitor',
    color: 0x26c6da,
    priceSensitivity: 0.4,
    walkingSensitivity: 0.4,
    serviceExpectation: 0.6,
    minDurationMinutes: 1 * 60,
    maxDurationMinutes: 4 * 60,
    evRequired: false,
    spawnWeight: 2,
  },
  resident: {
    id: 'resident',
    name: 'Resident',
    color: 0x9ccc65,
    priceSensitivity: 0.6,
    walkingSensitivity: 0.5,
    serviceExpectation: 0.4,
    minDurationMinutes: 4 * 60,
    maxDurationMinutes: 12 * 60,
    evRequired: false,
    spawnWeight: 2,
  },
  vip: {
    id: 'vip',
    name: 'VIP',
    color: 0xffd54f,
    priceSensitivity: 0.15,
    walkingSensitivity: 0.9,
    serviceExpectation: 0.9,
    minDurationMinutes: 1 * 60,
    maxDurationMinutes: 4 * 60,
    evRequired: false,
    spawnWeight: 1,
  },
  ev: {
    id: 'ev',
    name: 'EV Driver',
    color: 0x66bb6a,
    priceSensitivity: 0.5,
    walkingSensitivity: 0.6,
    serviceExpectation: 0.6,
    minDurationMinutes: 1 * 60,
    maxDurationMinutes: 3 * 60,
    evRequired: true,
    spawnWeight: 1,
  },
};

/**
 * Types eligible to spawn in Phase 3. EV drivers are excluded until EV chargers
 * exist (Phase 8), since their requirement can't be satisfied yet.
 */
export const SPAWNABLE_TYPES: string[] = [
  'office',
  'shopper',
  'hospital',
  'resident',
  'vip',
];
